import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Field } from './Modal';
import { Chip } from './StatusChip';
import ErrorState from './ErrorState';
import EmptyState from './EmptyState';
import { useConfirm } from './ConfirmDialog';
import { apiFetch, apiErrorMessage } from '../hooks/useApi';
import { formatDate, plural, todayLocalISO } from '../utils/format';

/**
 * <CompetencySharing /> — create & manage secure, no-login links that give another
 * contractor a read-only view of a chosen staff member's (or the whole team's)
 * qualifications: name, competency level, expiry + status, plus certificates on file.
 * Name + role only — never email/phone. Links can carry an access code, expire, and be
 * revoked. Admin-only (the API enforces this). Rendered from the Staff page.
 */
const NEW_INIT = { label: '', staffIds: [], wholeTeam: false, show_expired: true, expires_at: '', access_code: '' };
const MIN_CODE = 6;

const linkState = (l) => (l.revoked ? 'revoked' : (l.expires_at && new Date(l.expires_at) < new Date()) ? 'expired' : 'active');
const STATE_CHIP = { active: ['success', 'Active'], expired: ['warning', 'Expired'], revoked: ['danger', 'Revoked'] };

const Stat = ({ value, label }) => (
  <div className="cw-card cw-card-pad" style={{ flex: '1 1 min(120px, 100%)' }}>
    <div style={{ fontSize: 'var(--cs-text-2xl)', fontWeight: 700, color: 'var(--cw-text)' }}>{value}</div>
    <div className="cw-label">{label}</div>
  </div>
);

const copyText = async (text) => {
  if (!text || !navigator.clipboard?.writeText) return false;
  try { await navigator.clipboard.writeText(text); return true; } catch { return false; }
};

const CompetencySharing = ({ preselectStaffId = null }) => {
  const { confirm } = useConfirm();
  const [links, setLinks] = useState([]);
  const [linksLoading, setLinksLoading] = useState(true);
  const [linksError, setLinksError] = useState(null);
  const [staff, setStaff] = useState([]);
  const [staffError, setStaffError] = useState(null);
  const [form, setForm] = useState(() => (preselectStaffId ? { ...NEW_INIT, staffIds: [preselectStaffId] } : NEW_INIT));
  const [errors, setErrors] = useState({});
  const [creating, setCreating] = useState(false);
  const [query, setQuery] = useState('');
  const [justCreated, setJustCreated] = useState(null);

  const load = useCallback(async () => {
    setLinksLoading(true);
    try { const d = await apiFetch('/api/contractor/competency-links'); setLinks(Array.isArray(d?.links) ? d.links : []); setLinksError(null); }
    catch (e) { setLinksError(e); }
    finally { setLinksLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const loadStaff = useCallback(async () => {
    try {
      const d = await apiFetch('/api/contractor/staff');
      const list = Array.isArray(d) ? d : (d?.staff || d?.data || []);
      setStaff(list.filter((x) => x.id != null).map((x) => ({ id: x.id, name: x.name || [x.first_name, x.last_name].filter(Boolean).join(' ') || x.email || `Member ${x.id}`, role: x.role_title || x.role || '' })));
      setStaffError(null);
    } catch (e) { setStaffError(e); }
  }, []);
  useEffect(() => { loadStaff(); }, [loadStaff]);

  const stats = useMemo(() => ({
    total: links.length,
    active: links.filter((l) => linkState(l) === 'active').length,
    coded: links.filter((l) => l.has_code && linkState(l) === 'active').length,
    views: links.reduce((a, l) => a + (l.view_count || 0), 0),
  }), [links]);

  const filteredStaff = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? staff.filter((x) => x.name.toLowerCase().includes(q)) : staff;
  }, [staff, query]);

  const setF = (patch) => { setForm((f) => ({ ...f, ...patch })); setErrors((e) => { const n = { ...e }; Object.keys(patch).forEach((k) => { delete n[k]; }); if (patch.staffIds || patch.wholeTeam !== undefined) delete n.staffIds; return n; }); };
  const toggleStaff = (id) => setF({ staffIds: form.staffIds.includes(id) ? form.staffIds.filter((x) => x !== id) : [...form.staffIds, id] });

  const create = async () => {
    const errs = {};
    if (!form.wholeTeam && !form.staffIds.length) errs.staffIds = 'Choose team members, or share the whole team';
    const code = form.access_code.trim();
    if (code && code.length < MIN_CODE) errs.access_code = `Use at least ${MIN_CODE} characters — short codes are easy to guess`;
    if (form.expires_at && form.expires_at <= todayLocalISO()) errs.expires_at = 'Pick a date after today';
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setCreating(true);
    try {
      const staff_ids = form.wholeTeam ? [] : form.staffIds;
      const res = await apiFetch('/api/contractor/competency-links', { method: 'POST', body: JSON.stringify({ label: form.label, staff_ids, show_expired: form.show_expired, expires_at: form.expires_at || undefined, access_code: code }) });
      const copied = await copyText(res?.url);
      toast.success(copied ? 'Link created and copied' : 'Competency link created');
      setJustCreated(res?.url ? { url: res.url, copied } : null);
      setForm(NEW_INIT); setQuery('');
      load();
    } catch (e) { toast.error(`Couldn't create the link: ${apiErrorMessage(e)}`); }
    finally { setCreating(false); }
  };

  const copyLink = async (url) => {
    if (await copyText(url)) toast.success('Link copied');
    else toast.info('Your browser blocked copying — select the link text and copy it by hand.');
  };
  const revoke = async (l) => {
    const ok = await confirm({
      title: 'Revoke this link?',
      body: `Anyone holding “${l.label || 'this link'}” loses access immediately. It stays listed as revoked, as a record of what was shared.`,
      confirmLabel: 'Revoke link', tone: 'danger',
    });
    if (!ok) return;
    const before = links;
    setLinks((prev) => prev.map((x) => (x.id === l.id ? { ...x, revoked: 1 } : x)));
    try { await apiFetch(`/api/contractor/competency-links/${l.id}`, { method: 'PATCH', body: JSON.stringify({ revoked: true }) }); toast.success('Link revoked'); }
    catch (e) { setLinks(before); toast.error(`Couldn't revoke the link: ${apiErrorMessage(e)}`); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
        <Stat value={stats.total} label="Links created" />
        <Stat value={stats.active} label="Active" />
        <Stat value={stats.coded} label="Active with a code" />
        <Stat value={stats.views} label="Total views" />
      </div>

      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <section className="cw-card cw-card-pad" style={{ flex: '1 1 340px', minWidth: 0 }} aria-labelledby="cw-comp-new">
          <h3 id="cw-comp-new" className="cw-h3" style={{ marginBottom: 4 }}><i className="fa-solid fa-user-shield" style={{ color: 'var(--cw-blue)', marginRight: 8 }} aria-hidden="true" />New competency link</h3>
          <p className="cw-meta" style={{ margin: '0 0 14px' }}>A read-only view of the team members you pick — qualifications, competency level and expiry, plus certificates on file. Shows name and role only.</p>

          <Field label="Label (optional)">
            <input value={form.label} placeholder="e.g. Site induction — Acme FM" onChange={(e) => setF({ label: e.target.value })} />
          </Field>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--cs-text-sm)', cursor: 'pointer', margin: '12px 0 8px', minHeight: 44 }}>
            <input type="checkbox" checked={form.wholeTeam} onChange={(e) => setF({ wholeTeam: e.target.checked })} style={{ width: 18, height: 18 }} />
            <span style={{ fontWeight: 600 }}>Share the whole team</span>
          </label>

          {!form.wholeTeam && (
            <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
              <legend className="cw-label" style={{ marginBottom: 4 }}>Team members <span className="cw-req" aria-hidden="true">*</span></legend>
              {form.staffIds.length > 0 && <p className="cw-meta" style={{ margin: '0 0 6px' }} aria-live="polite">{plural(form.staffIds.length, 'member')} selected</p>}
              {staffError ? <ErrorState compact thing="your team" error={staffError} onRetry={loadStaff} />
                : staff.length === 0 ? <p className="cw-meta" style={{ margin: 0 }}>No team members yet.</p>
                : (
                  <>
                    {staff.length > 6 && (
                      <input type="search" className="cw-input" style={{ marginBottom: 6 }} value={query} aria-label="Search team" placeholder="Search team…" onChange={(e) => setQuery(e.target.value)} />
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 200, overflowY: 'auto', border: `1px solid ${errors.staffIds ? 'var(--cw-danger)' : 'var(--cw-border-light)'}`, borderRadius: 8, padding: 6 }}>
                      {filteredStaff.map((x) => {
                        const on = form.staffIds.includes(x.id);
                        return (
                          <label key={x.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--cs-text-sm)', cursor: 'pointer', padding: 6, minHeight: 40, borderRadius: 6, background: on ? 'var(--cw-blue-tint)' : 'transparent' }}>
                            <input type="checkbox" checked={on} onChange={() => toggleStaff(x.id)} style={{ width: 18, height: 18 }} />
                            <span>{x.name}{x.role ? <span className="cw-meta"> · {x.role}</span> : null}</span>
                          </label>
                        );
                      })}
                      {filteredStaff.length === 0 && <div className="cw-meta" style={{ padding: 6 }}>No matches.</div>}
                    </div>
                  </>
                )}
              {errors.staffIds && <p className="cw-field-error" role="alert"><i className="fa-solid fa-circle-exclamation" aria-hidden="true" /> {errors.staffIds}</p>}
            </fieldset>
          )}

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--cs-text-sm)', cursor: 'pointer', margin: '12px 0', minHeight: 44 }}>
            <input type="checkbox" checked={form.show_expired} onChange={(e) => setF({ show_expired: e.target.checked })} style={{ width: 18, height: 18 }} /> Include expired qualifications
          </label>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(160px, 100%), 1fr))', gap: 12 }}>
            <Field label="Expires" error={errors.expires_at} hint={form.expires_at ? undefined : 'Leave blank for the default expiry'}>
              <input type="date" min={todayLocalISO()} value={form.expires_at} onChange={(e) => setF({ expires_at: e.target.value })} />
            </Field>
            <Field label="Access code (optional)" error={errors.access_code} hint={`At least ${MIN_CODE} characters`}>
              <input value={form.access_code} autoComplete="off" spellCheck={false} placeholder="word or PIN" onChange={(e) => setF({ access_code: e.target.value })} />
            </Field>
          </div>

          <div role="note" style={{ display: 'flex', alignItems: 'flex-start', gap: 8, background: form.access_code.trim() ? 'var(--cw-success-tint)' : 'var(--cw-warning-tint)', border: `1px solid ${form.access_code.trim() ? 'var(--cs-green-border, #bbf7d0)' : 'var(--cw-warning-border)'}`, borderRadius: 8, padding: '8px 10px', margin: '4px 0 14px' }}>
            <i className={`fa-solid ${form.access_code.trim() ? 'fa-shield-halved' : 'fa-triangle-exclamation'}`} style={{ color: form.access_code.trim() ? 'var(--cw-success-fg)' : 'var(--cw-warning-fg)', marginTop: 2 }} aria-hidden="true" />
            <span style={{ fontSize: 'var(--cs-text-sm)', color: 'var(--cw-text-2)' }}>
              {form.access_code.trim()
                ? 'Protected — the viewer must enter this code. You’re disclosing staff names, roles and qualifications; send the code separately (text or call), not with the link.'
                : 'Link only — anyone with the link can view named staff qualifications. Add an access code for more protection, and only share what your team consents to disclose.'}
            </span>
          </div>

          <button type="button" className="cw-btn cw-btn-primary" onClick={create} disabled={creating} aria-busy={creating || undefined} style={{ width: '100%', justifyContent: 'center' }}><i className="fa-solid fa-link" aria-hidden="true" /> {creating ? 'Creating…' : 'Create secure link'}</button>
          {justCreated && (
            <div role="status" style={{ marginTop: 12, fontSize: 'var(--cs-text-sm)' }}>
              <div style={{ fontWeight: 600 }}>{justCreated.copied ? 'Copied to your clipboard:' : 'Your new link (copy it from here):'}</div>
              <input readOnly className="cw-input" value={justCreated.url} aria-label="New competency link" onFocus={(e) => e.target.select()} style={{ marginTop: 4, fontFamily: 'ui-monospace, monospace' }} />
            </div>
          )}
        </section>

        <section style={{ flex: '2 1 380px', minWidth: 0 }} aria-labelledby="cw-comp-links">
          <h3 id="cw-comp-links" style={{ fontSize: 'var(--cs-text-base)', fontWeight: 700, margin: '0 0 10px' }}>Share links {links.length > 0 && <span className="cw-meta" style={{ fontWeight: 500 }}>({links.length})</span>}</h3>
          {linksError ? <ErrorState thing="competency links" error={linksError} onRetry={load} retrying={linksLoading} />
            : linksLoading && !links.length ? <div role="status" className="cw-meta">Loading links…</div>
            : links.length === 0 ? (
              <EmptyState icon="fa-solid fa-share-nodes" title="No share links yet" body="Create one to share your team’s competency." />
            ) : (
              <ul style={{ display: 'flex', flexDirection: 'column', gap: 10, listStyle: 'none', margin: 0, padding: 0 }}>
                {links.map((l) => {
                  const st = linkState(l);
                  const [tone, label] = STATE_CHIP[st];
                  const live = st === 'active';
                  const count = (l.staff_ids || []).length;
                  const name = l.label || (l.scope_type === 'team' ? 'Whole team' : 'Selected staff');
                  return (
                    <li key={l.id} className="cw-card" style={{ padding: 14, background: live ? undefined : 'var(--cw-surface-alt)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                        <span style={{ fontSize: 'var(--cs-text-base)', fontWeight: 700 }}>{name}</span>
                        <Chip tone={tone} label={label} dot />
                        {l.has_code
                          ? <Chip tone="success" label={<><i className="fa-solid fa-lock" aria-hidden="true" /> Access code</>} />
                          : <Chip tone="warning" label={<><i className="fa-solid fa-lock-open" aria-hidden="true" /> Link only</>} />}
                      </div>
                      <div className="cw-meta" style={{ marginBottom: 8 }}>
                        {l.scope_type === 'team' ? 'Whole team' : plural(count, 'member')} · {plural(l.view_count || 0, 'view')}
                        {l.last_viewed_at && ` · last viewed ${formatDate(l.last_viewed_at)}`}
                        {l.expires_at && <> · <span style={{ color: st === 'expired' ? 'var(--cw-danger-fg)' : undefined }}>{st === 'expired' ? 'expired' : 'expires'} {formatDate(l.expires_at)}</span></>}
                      </div>
                      {st !== 'revoked' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          {live && (
                            <>
                              <input readOnly className="cw-input" value={l.url} aria-label={`Link for ${name}`} onFocus={(e) => e.target.select()}
                                style={{ flex: '1 1 200px', minWidth: 0, fontFamily: 'ui-monospace, monospace', fontSize: 'var(--cs-text-sm)' }} />
                              <button type="button" className="cw-btn cw-btn-secondary" onClick={() => copyLink(l.url)} aria-label={`Copy link for ${name}`}><i className="fa-solid fa-copy" aria-hidden="true" /> Copy</button>
                              <a className="cw-btn cw-btn-secondary" href={l.url} target="_blank" rel="noopener noreferrer"><i className="fa-solid fa-eye" aria-hidden="true" /> Preview<span className="cw-sr-only"> {name} (opens in a new tab and counts as a view)</span></a>
                            </>
                          )}
                          <button type="button" className="cw-btn cw-btn-danger-outline" style={{ marginLeft: live ? undefined : 'auto' }} onClick={() => revoke(l)} aria-label={`Revoke ${name}`}><i className="fa-solid fa-ban" aria-hidden="true" /> Revoke</button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
        </section>
      </div>
    </div>
  );
};

export default CompetencySharing;
